import { getFailureDetailFromCatalog, getCatalogTestName } from "./preuseTestCatalog";
import { TEST_NAME_MAP } from "./testNameMap";
import { getExpiratoryCalibrationScalarFromRow, isExpiratoryFlowSensorCalibrationRow } from "../utils/preuseErrorDecode";

/** 32-bit unsigned error code as 8 hex digits */
export function formatErrorCode32(code) {
  const n = Number(code);
  if (!Number.isFinite(n)) return "00000000";
  return (n >>> 0).toString(16).toUpperCase().padStart(8, "0");
}

function firstSensorValue(row) {
  if (isExpiratoryFlowSensorCalibrationRow(row)) {
    return String(getExpiratoryCalibrationScalarFromRow(row));
  }
  if (row.raw_data !== undefined) {
    if (Array.isArray(row.raw_data)) {
      const arr = row.raw_data;
      if (arr.length === 0) return "—";
      if (arr.length <= 12) return arr.join(", ");
      return `${arr.length} samples · ${arr.slice(0, 6).join(", ")}, …`;
    }
    if (typeof row.raw_data === "number") return String(row.raw_data);
    return String(row.raw_data);
  }
  const v = row.data?.[0]?.data;
  return v !== undefined && v !== null ? String(v) : "—";
}

function friendlyTitle(testid) {
  const raw = TEST_NAME_MAP[testid] || getCatalogTestName(testid);
  if (!raw) return `TEST_${testid}`;
  return raw;
}

/**
 * Fallback when `test_id` is missing from `preuse_tests.json`.
 * @param {object} row
 */
function getFailureDetailFallback(row) {
  const testid = Number(row.testid);
  const masked = Number(row.errorcode) >>> 0;
  const hex = formatErrorCode32(row.errorcode);
  const showErrorCodeBadge = masked !== 0;

  const name = friendlyTitle(testid);
  const troubleshootStepsFallback = [
    "No catalogue entry exists for this test id in preuse_tests.json.",
    ...(showErrorCodeBadge ? [`Error code bitmap: ERROR_CODE=${hex}`] : []),
  ];

  const serviceFallback =
    "Add this test_id to `preuse_tests.json` or contact service.";

  return {
    moduleTitle: name.toUpperCase(),
    categorySubtitle: "Pre-use diagnostic check",
    sensorLine: `${name} value : ${firstSensorValue(row)}`,
    troubleshootSteps: troubleshootStepsFallback,
    serviceAction: serviceFallback,
    faultCards: [
      {
        bitId: "ERR",
        title: name.replace(/_/g, " "),
        subtitle: "Pre-use diagnostic check",
        troubleshootSteps: troubleshootStepsFallback,
        serviceAction: serviceFallback,
      },
    ],
    primaryCheckId: "B0",
    primaryProbeTitle: name.replace(/_/g, " "),
    relatedChecks: [],
    testName: name,
    errorHex: hex,
    showErrorCodeBadge,
  };
}

/**
 * Combines decoded row + catalogue data for `/detail`.
 * Uses `src/preuse_tests.json` via `preuseTestCatalog.js`.
 */
export function getFailureDetail(row) {
  const fromCatalog = getFailureDetailFromCatalog(row);
  if (fromCatalog) {
    return fromCatalog;
  }
  return getFailureDetailFallback(row);
}
